import { useEffect, useState } from 'react'
import { useRouter } from '@/i18n/navigation'
import { buildAdminLoginUrl } from '@/lib/redirects'
import { useAdminAuth, useAgentAuth } from '@/stores/auth'
import type { UserRole } from '@/types/roles'
import { useParams } from 'next/navigation'
import { userHasAnyRole } from '@/lib/roles'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * Hook for protecting admin routes
 * Automatically redirects to login if not authenticated
 * Supports role-based access control
 */
export const useAdminAuthGuard = (allowedRoles?: UserRole[]) => {
  const router = useRouter()
  const { isAuthenticated, isLoading, user } = useAdminAuth()
  const [roleCheckLoading, setRoleCheckLoading] = useState(false)
  const params = useParams()
  const locale = params.locale as string

  useEffect(() => {
    let isMounted = true

    const checkAuth = async () => {
      // Wait a brief moment to allow for store rehydration from localStorage
      await new Promise(resolve => setTimeout(resolve, 0))

      if (!isMounted) return

      const redirectToLogin = () => {
        if (typeof window !== 'undefined') {
          const current = window.location.pathname + window.location.search
          router.push(`/admin?next=${encodeURIComponent(current)}`)
        } else {
          router.push('/admin')
        }
      }

      if (!isAuthenticated && !isLoading) {
        // Rely on the in-memory/persisted auth store only. If not authenticated,
        // redirect to the admin login page and preserve current URL so the
        // user can return after successful login.
        if (isMounted) {
          redirectToLogin()
        }
        return
      }

      // Check role-based access if roles are specified
      if (allowedRoles && user) {
        const hasRequiredRole = userHasAnyRole(user, allowedRoles)

        if (!hasRequiredRole && isMounted) {
          redirectToLogin()
          return
        }
      }

      if (isAuthenticated && user) {
        const rawPermissions = Array.isArray((user as any).permissions)
          ? ((user as any).permissions as string[])
          : []

        const normalizedPermissions = new Set(
          rawPermissions
            .filter(
              (code): code is string =>
                typeof code === 'string' && code.trim().length > 0
            )
            .map(code => code.trim().toUpperCase())
        )

        const hasDashboardAccess =
          normalizedPermissions.has(PERMISSIONS.ANY) ||
          normalizedPermissions.has(PERMISSIONS.ADMIN_DASHBOARD)

        if (!hasDashboardAccess) {
          redirectToLogin()
          return
        }
      }
    }

    checkAuth()

    return () => {
      isMounted = false
    }
  }, [isAuthenticated, isLoading, router, user, allowedRoles, locale])

  return {
    isAuthenticated,
    isLoading: isLoading || (!user && isAuthenticated),
    user,
    loading: isLoading || (!user && isAuthenticated),
  }
}

/**
 * Hook for protecting agent routes
 * Automatically redirects to login if not authenticated
 */
export const useAgentAuthGuard = () => {
  const router = useRouter()
  const { isAuthenticated, verify, isLoading } = useAgentAuth()

  useEffect(() => {
    let isCancelled = false

    const checkAuth = async () => {
      // Wait a brief moment to allow for store rehydration from localStorage
      await new Promise(resolve => setTimeout(resolve, 0))

      if (isCancelled) return

      if (!isAuthenticated && !isLoading) {
        if (!isCancelled) {
          router.push('/agent')
        }
      }
    }

    checkAuth()

    return () => {
      isCancelled = true
    }
  }, [isAuthenticated, isLoading, verify, router])

  return { isAuthenticated, isLoading }
}

/**
 * Hook for login pages - redirects to dashboard if already authenticated
 */
export const useLoginRedirect = (userType: 'admin' | 'agent') => {
  const router = useRouter()
  const adminAuth = useAdminAuth()
  const agentAuth = useAgentAuth()

  useEffect(() => {
    let isCancelled = false

    // Redirects on login pages. Avoid calling verify() automatically to
    // prevent unexpected logout — instead rely on persisted session state
    // (isAuthenticated) or the presence of a stored token.
    const checkAndRedirect = async () => {
      // Wait a brief moment to allow for store rehydration from localStorage
      await new Promise(resolve => setTimeout(resolve, 0))

      if (isCancelled) return

      if (userType === 'admin') {
        if (adminAuth.isAuthenticated) {
          if (!isCancelled) {
            router.push('/admin/dashboard')
          }
          return
        }

        // Rely on the store's isAuthenticated flag; no localStorage/cookie fallbacks.
      } else if (userType === 'agent') {
        if (agentAuth.isAuthenticated) {
          if (!isCancelled) {
            router.push('/agent/dashboard')
          }
          return
        }
      }
    }

    checkAndRedirect()

    return () => {
      isCancelled = true
    }
  }, [
    userType,
    adminAuth.isAuthenticated,
    agentAuth.isAuthenticated,
    adminAuth.isLoading,
    agentAuth.isLoading,
    router,
  ])
  return {
    isLoading: userType === 'admin' ? adminAuth.isLoading : agentAuth.isLoading,
  }
}

/**
 * Hook for automatic token refresh
 * Periodically verifies tokens to ensure they're still valid
 */
export const useTokenRefresh = () => {
  useAdminAuth()
  useAgentAuth()
}
